import { DatePipe, DecimalPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, OnInit, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faLocationDot, faTrash, faUpload } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { SelectionMode } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'
import { forkJoin } from 'rxjs'
import { finalize } from 'rxjs/operators'

import { BaseComponent, FeatureType, Grid, ListItem, VpJob } from '../../shared'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

interface PlansSpecsFile {
    fileName: string
    url: string
    size: number
    lastModified: string
}

@Component({
    selector: 'app-plans-specs',
    templateUrl: './plans-specs.component.html',
    styleUrls: ['./plans-specs.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        WjInputModule,
        WjGridModule,
        WjGridFilterModule,
        AutoSizeDirective,
        DatePipe,
        DecimalPipe,
        FaIconComponent,
        NgbNav,
        NgbNavItem,
        NgbNavLink,
        NgbNavLinkBase,
    ],
})
export class PlansSpecsComponent extends BaseComponent implements OnInit {
    private route = inject(ActivatedRoute)
    private restoringFromUrl = false

    faTrash = faTrash
    faUpload = faUpload
    faMap = faLocationDot
    selectedJob = signal<string>('')
    jobMap: ListItem<string>[] = []
    allJobs: VpJob[] = []
    grid: Grid<PlansSpecsFile>
    active = 1
    addressString = ''
    checkedFiles = new Set<string>()

    // Tab categories
    readonly categories = [
        { id: 1, name: 'Bid Sets', directory: 'BidSets' },
        { id: 2, name: 'Conform Sets', directory: 'ConformSets' },
        { id: 3, name: 'Docs', directory: 'Docs' },
        { id: 4, name: 'Photos', directory: 'Photos' },
        { id: 5, name: 'Schedules', directory: 'Schedules' },
        { id: 6, name: 'Submittals', directory: 'Submittals' },
        { id: 7, name: 'Condensed Specs', directory: 'CondensedSpecs' },
    ]

    get activeCategory() {
        return this.categories.find(c => c.id === this.active) || this.categories[0]
    }

    constructor() {
        super()
        this.grid = new Grid<PlansSpecsFile>(this.services)
            .filterBy(item => [item.fileName])
            .isReadOnly(true)
            .selectionMode(SelectionMode.None)
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPlansAndSpecsEdit])
    }

    ngOnInit() {
        this.loadJobs()
    }

    loadJobs() {
        this.cancelableRequest(
            'loadJobs',
            true,
            this.services.data
                .load(true, true, this.services.data.vpJobs.queryAll())
                .pipe(finalize(() => this.initialLoadingComplete()))
                .subscribe({
                    next: (result: unknown[]) => {
                        this.allJobs = result[0] as VpJob[]
                        this.buildJobMap()
                        this.restoreFromUrl()
                    },
                    error: () => {
                        this.services.notification.error('Failed to load projects')
                    },
                }),
        )
    }

    buildJobMap() {
        const jobMap: ListItem<string>[] = []
        jobMap.push(new ListItem<string>('', 'Select a Project'))

        // Add active projects
        this.allJobs.forEach(job => {
            jobMap.push(new ListItem<string>(job.id, job.id + ' ' + job.name))
        })

        this.jobMap = jobMap
    }

    onJobChanged() {
        if (this.restoringFromUrl) {
            return
        }

        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            this.loadFiles(jobId)
            this.loadJobAddress(jobId)
        } else {
            this.grid.items = []
            this.addressString = ''
        }

        this.syncStateToUrl()
    }

    loadJobAddress(jobId: string) {
        const params = new HttpParams().set('jobId', jobId)
        this.cancelableRequest(
            'loadJobAddress',
            false,
            this.services.data.get('/api/VpJob/GetJobAddress', params).subscribe({
                next: (result: any) => {
                    if (result && result.hasCompleteAddress) {
                        const parts: string[] = []
                        if (result.mailAddress) parts.push(result.mailAddress)
                        if (result.shipCity) parts.push(result.shipCity)
                        if (result.shipState) parts.push(result.shipState)
                        this.addressString = parts.join(', ')
                    } else {
                        this.addressString = 'No Address Found'
                    }
                },
                error: () => {
                    this.addressString = 'No Address Found'
                },
            }),
        )
    }

    navigate() {
        if (!this.addressString || this.addressString === 'No Address Found') {
            this.services.notification.info('No Address was found for the selected job')
            return
        }

        window.open('http://maps.google.com/?q=' + this.addressString, '_blank')
    }

    tabChanged() {
        if (this.restoringFromUrl) {
            return
        }

        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            this.loadFiles(jobId)
        }

        this.syncStateToUrl()
    }

    private syncStateToUrl() {
        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            this.services.angular.setQueryParams({ jobId, tab: this.active }, this.route)
        } else {
            this.services.angular.setQueryParams({ jobId: null, tab: null }, this.route)
        }
    }

    private restoreFromUrl() {
        const jobId = this.services.angular.getQueryParam('jobId', this.route)
        if (!jobId) {
            return
        }

        if (!this.allJobs.some(j => j.id === jobId)) {
            return
        }

        const tabParam = this.services.angular.getQueryParam('tab', this.route)
        let tab = 1
        if (tabParam) {
            const parsed = parseInt(tabParam, 10)
            if (this.categories.some(c => c.id === parsed)) {
                tab = parsed
            }
        }

        this.restoringFromUrl = true
        this.active = tab
        this.selectedJob.set(jobId)
        this.loadFiles(jobId)
        this.loadJobAddress(jobId)
        setTimeout(() => {
            this.restoringFromUrl = false
        }, 0)
    }

    loadFiles(jobId: string) {
        this.checkedFiles.clear()
        const category = this.activeCategory.directory
        const params = new HttpParams().set('jobId', jobId).set('category', category)
        this.cancelableRequest(
            'loadFiles',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/PlansSpecs/GetFiles', params)).subscribe({
                next: result => {
                    this.grid.items = result[0] as PlansSpecsFile[]
                },
                error: () => {
                    this.services.notification.error('Failed to load files')
                    this.grid.items = []
                },
            }),
        )
    }

    onFileChanged(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input.files
        if (!files || files.length === 0) {
            return
        }

        const selectedJobId = this.selectedJob()
        if (!selectedJobId || selectedJobId === '') {
            this.services.notification.warning('Please select a project before uploading files')
            input.value = ''
            return
        }

        // Get file names from the selected files
        const fileNames = Array.from(files).map(f => f.name)

        // Check for existing files first
        const category = this.activeCategory.directory
        this.services.data
            .post<{ existingFiles?: string[]; ExistingFiles?: string[] }>('/api/PlansSpecs/CheckExistingFiles', {
                jobId: selectedJobId,
                fileNames: fileNames,
                category: category,
            })
            .subscribe({
                next: result => {
                    const existingFiles = result.existingFiles || result.ExistingFiles || []
                    if (existingFiles.length > 0) {
                        const existingFileNames = existingFiles.join(', ')
                        const replaceMessage =
                            existingFiles.length === 1
                                ? `The file "${existingFileNames}" already exists. Do you want to replace it?`
                                : `The following ${existingFiles.length} file(s) already exist:\n\n${existingFileNames}\n\nDo you want to replace them?`

                        this.services.modals.confirm('Replace Existing Files?', replaceMessage).then(
                            () => {
                                this.checkEmailAndUpload(files, selectedJobId, input)
                            },
                            () => {
                                // User cancelled
                                input.value = ''
                            },
                        )
                    } else {
                        // No existing files, proceed with email check and upload
                        this.checkEmailAndUpload(files, selectedJobId, input)
                    }
                },
                error: () => {
                    this.services.notification.error('Failed to check for existing files')
                    input.value = ''
                },
            })
    }

    checkEmailAndUpload(files: FileList, jobId: string, input: HTMLInputElement) {
        // Only prompt for email if uploading to Conform Sets tab (category id 2)
        if (this.active === 2) {
            this.services.modals
                .confirm(
                    'Send Email Notification?',
                    "Would you like to send an email notification to this job's super about the new Conform Sets upload?",
                )
                .then(
                    () => {
                        // User chose Yes
                        this.uploadFiles(files, jobId, input, true)
                    },
                    () => {
                        // User chose No or cancelled
                        this.uploadFiles(files, jobId, input, false)
                    },
                )
        } else {
            // Not Conform Sets tab, proceed without email
            this.uploadFiles(files, jobId, input, false)
        }
    }

    uploadFiles(files: FileList, jobId: string, input: HTMLInputElement, sendEmail = false) {
        const formData: FormData = new FormData()
        formData.append('jobId', jobId)
        formData.append('category', this.activeCategory.directory)
        formData.append('sendEmail', sendEmail.toString())

        // Append all selected files
        for (const file of Array.from(files)) {
            formData.append('files', file, file.name)
        }

        const fileCount = files.length
        this.services.data.load(true, false, this.services.data.post('/api/PlansSpecs/UploadFiles', formData)).subscribe({
            next: () => {
                this.services.notification.success(`Successfully uploaded ${fileCount} file(s)`)
                // Clear the file input
                input.value = ''
                // Refresh the plans and specs list
                this.loadFiles(jobId)
            },
            error: errorResult => {
                const errorMessage = errorResult.error?.message || errorResult.error || 'Failed to upload files'
                this.services.notification.error('The file(s) could not be uploaded. ' + errorMessage)
                // Clear the file input even on error
                input.value = ''
            },
        })
    }

    deleteFiles() {
        if (this.checkedFiles.size === 0) {
            this.services.notification.warning('Please select one or more files to delete')
            return
        }

        const selectedFiles = this.grid.items.filter(f => this.checkedFiles.has(f.fileName))
        const fileNames = selectedFiles.map(f => f.fileName).join(', ')
        const fileCount = selectedFiles.length

        this.services.modals.confirm('Confirm Delete', `Are you sure you want to delete ${fileCount} file(s)?\n\nFiles: ${fileNames}`).then(
            () => {
                const selectedJobId = this.selectedJob()
                if (!selectedJobId || selectedJobId === '') {
                    this.services.notification.error('No project selected')
                    return
                }

                const category = this.activeCategory.directory
                const deleteRequests = selectedFiles.map(file => {
                    const params = new HttpParams().set('jobId', selectedJobId).set('fileName', file.fileName).set('category', category)
                    const options = { params: params }
                    return this.services.data.http.delete('/api/PlansSpecs/DeleteFile', options)
                })

                forkJoin(deleteRequests).subscribe({
                    next: () => {
                        this.services.notification.success(`Successfully deleted ${fileCount} file(s)`)
                        this.checkedFiles.clear()
                        this.loadFiles(selectedJobId)
                    },
                    error: () => {
                        this.services.notification.error('Failed to delete one or more files')
                        this.loadFiles(selectedJobId)
                    },
                })
            },
            () => {
                // User cancelled
            },
        )
    }

    get hasSelectedFiles(): boolean {
        return this.checkedFiles.size > 0
    }

    toggleRowSelection(item: PlansSpecsFile, checked: boolean) {
        if (checked) {
            this.checkedFiles.add(item.fileName)
        } else {
            this.checkedFiles.delete(item.fileName)
        }
    }
}
